import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Wordmark from "@/components/Wordmark";

type State = "checking" | "ready" | "invalid" | "done";

const ResetPassword = () => {
  const [state, setState] = useState<State>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let settled = false;
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        settled = true;
        setState("ready");
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        settled = true;
        setState("ready");
      }
    });
    // An expired or already-used link never produces a session.
    const timer = window.setTimeout(() => {
      if (!settled) setState((current) => (current === "checking" ? "invalid" : current));
    }, 4000);
    return () => {
      window.clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < 8) return setError("Password must be at least 8 characters");
    if (password.length > 72) return setError("Password must be 72 characters or fewer");
    if (password !== confirm) return setError("Passwords do not match");
    setError(null);
    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (updateError) return setError(updateError.message);
    setState("done");
  };

  return (
    <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card rounded-[1.5rem] shadow-bold p-8">
        <Link to="/" className="flex justify-center mb-6">
          <Wordmark size="md" />
        </Link>
        <h1 className="font-display text-3xl text-center">Choose a new password</h1>

        {state === "checking" && (
          <p className="mt-8 text-center text-sm text-muted-foreground">Checking your reset link…</p>
        )}

        {state === "invalid" && (
          <div className="mt-6 space-y-4 text-center">
            <p role="alert" className="text-sm text-foreground">
              This reset link has expired or has already been used. Request a new one to continue.
            </p>
            <Link
              to="/forgot-password"
              className="block w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors"
            >
              Request a new link
            </Link>
          </div>
        )}

        {state === "done" && (
          <div className="mt-6 space-y-4 text-center">
            <p role="status" className="text-sm text-foreground">
              Your password has been updated. You can sign in with it now.
            </p>
            <Link
              to="/auth"
              className="block w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors"
            >
              Go to sign in
            </Link>
          </div>
        )}

        {state === "ready" && (
          <form onSubmit={submit} className="space-y-4 mt-6" noValidate>
            <div>
              <label htmlFor="new-password" className="text-xs font-semibold uppercase tracking-wider">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
                maxLength={72}
                autoComplete="new-password"
                className="w-full mt-1 px-4 py-3 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-wider">
                Confirm password
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                required
                minLength={8}
                maxLength={72}
                autoComplete="new-password"
                className="w-full mt-1 px-4 py-3 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
